/**
 * Linhas dos gráficos de linha do tempo dos relatórios.
 *
 * O rótulo do PO (`po_label`, ex.: "PO 05/26") vem pronto do backend, que é
 * quem aplica a regra "número do PO = mês de início". Aqui só se distribui o
 * realizado de cada série na coluna do mês correspondente.
 */
export interface TimelineMonthColumn {
  po_label: string;
  reference_label: string;
}

export interface TimelineSeries {
  values?: { realized?: number | null }[];
}

export type TimelineChartRow = Record<string, string | number>;

export const buildTimelineChartRows = <S extends TimelineSeries>(
  months: TimelineMonthColumn[],
  series: S[],
  seriesKey: (item: S) => string
): TimelineChartRow[] =>
  months.map((month, monthIndex) => {
    const row: TimelineChartRow = {
      po_label: month.po_label,
      reference_label: month.reference_label,
    };

    series.forEach((item) => {
      row[seriesKey(item)] = Number(item.values?.[monthIndex]?.realized || 0);
    });

    return row;
  });

/** Acima desta quantidade de meses o rótulo do PO inclina para caber no eixo. */
export const PO_AXIS_TILT_THRESHOLD = 6;
const PO_AXIS_TILT_ANGLE = -35;
const PO_AXIS_TILTED_HEIGHT = 72;
const PO_AXIS_VERTICAL_ANGLE = -90;
const PO_AXIS_VERTICAL_HEIGHT = 84;
/** Recuo do eixo nas pontas: meia largura de "PO 00/00", centralizado no ponto. */
export const PO_AXIS_EDGE_PADDING = 32;

/**
 * Propriedades do eixo X com rótulos de PO.
 *
 * Por padrão o Recharts esconde rótulos que não cabem no eixo, e o gráfico
 * parece ter pulado um PO. `interval: 0` obriga a mostrar todos; com muitos
 * meses, o texto inclina e, em tela compacta, fica na vertical, que é o único
 * jeito de caber um rótulo por coluna. Na horizontal o rótulo fica centralizado
 * no ponto, então o eixo recua nas pontas para o primeiro e o último não serem
 * cortados pela borda do gráfico.
 */
export const getPoAxisProps = (monthCount: number, compact = false) => {
  if (monthCount <= PO_AXIS_TILT_THRESHOLD) {
    return {
      interval: 0 as const,
      padding: { left: PO_AXIS_EDGE_PADDING, right: PO_AXIS_EDGE_PADDING },
    };
  }

  return {
    interval: 0 as const,
    angle: compact ? PO_AXIS_VERTICAL_ANGLE : PO_AXIS_TILT_ANGLE,
    textAnchor: 'end' as const,
    height: compact ? PO_AXIS_VERTICAL_HEIGHT : PO_AXIS_TILTED_HEIGHT,
  };
};
